import { useCallback, useEffect, useRef, useState } from "react";

import { faPlus, faSearch } from "@fortawesome/pro-regular-svg-icons";
import { clamp } from "lodash-es";
import { FieldArrayWithId, useFieldArray, useWatch } from "react-hook-form";

import { Button } from "src/base-components/Button";
import { Divider } from "src/base-components/Divider";
import { TextConfirmationModal } from "src/base-components/TextConfirmationModal";
import { FormToggleAccordion } from "src/cases/templates/common/FormToggleAccordion";
import { TemplateFormEditor } from "src/cases/templates/common/TemplateFormEditor";
import { TemplateItemInfo } from "src/cases/templates/common/TemplateItemInfo";
import {
  ItemType,
  TemplateItemsList,
  TemplateListItem,
} from "src/cases/templates/common/TemplateItemList";
import { FormStepWrapper } from "src/cases/templates/FormStepWrapper";
import { getStepById } from "src/cases/templates/formSteps";
import {
  CaseTemplateFormValues,
  CaseTemplateTaskFormValue,
} from "src/cases/templates/formUtils/types";
import { generateInternalId } from "src/cases/templates/internalIdUtils";
import { useCheckFieldPermission } from "src/cases/templates/useCheckFieldPermission";
import { useWorkflowReferences } from "src/cases/templates/useWorkflowReferences";
import { Accordion } from "src/design-system/Accordion";
import { EmptyState } from "src/design-system/EmptyState";
import { useModal } from "src/design-system/Modal";

const TaskItem: React.FC<{
  index: number;
  active: boolean;
  onRemove: () => void;
  onSelect: () => void;
  canDelete: boolean;
}> = ({ active, index, onSelect, onRemove, canDelete }) => {
  const displayName = useWatch({
    name: `attributes.case_tasks.${index}.display_name`,
  });

  return (
    <TemplateListItem
      active={active}
      disabled={!canDelete}
      displayName={displayName}
      itemType={ItemType.TASK}
      onRemove={onRemove}
      onSelect={onSelect}
    />
  );
};

const Tasks: React.FC<{
  fields: FieldArrayWithId<
    CaseTemplateFormValues,
    "attributes.case_tasks",
    "_id"
  >[];
  activeTaskIndex: number;
  onSelectTask: (index: number) => void;
  onAddTask: () => void;
  onRemoveTask: (index: number) => void;
}> = ({ onSelectTask, fields, onAddTask, onRemoveTask, activeTaskIndex }) => {
  const hasPermission = useCheckFieldPermission();

  return (
    <div className="w-44 shrink-0">
      {!!fields?.length && (
        <TemplateItemsList>
          {fields.map((field, index) => (
            <TaskItem
              key={field._id}
              active={activeTaskIndex === index}
              canDelete={
                field._isNewlyAppended ||
                hasPermission("delete", "attributes.case_tasks")
              }
              index={index}
              onRemove={() => onRemoveTask(index)}
              onSelect={() => onSelectTask(index)}
            />
          ))}
        </TemplateItemsList>
      )}

      {hasPermission("add", "attributes.case_tasks") && (
        <Button
          dataLoc="add-task-button"
          iconLeft={faPlus}
          size="sm"
          variant="secondary"
          onClick={onAddTask}
        >
          Add task
        </Button>
      )}
    </div>
  );
};

const TaskKeyWatcher: React.FC<{
  taskIndex: number;
  internalId: string;
}> = ({ taskIndex, internalId }) => {
  const { updateTaskKey } = useWorkflowReferences();
  const previousKeyRef = useRef<string>();

  const currentKey = useWatch({
    name: `attributes.case_tasks.${taskIndex}.key`,
  });

  useEffect(() => {
    const normalizedCurrentKey = currentKey || "";

    if (previousKeyRef.current === undefined) {
      previousKeyRef.current = normalizedCurrentKey;
      return;
    }

    if (previousKeyRef.current !== normalizedCurrentKey && internalId) {
      updateTaskKey(internalId, normalizedCurrentKey);
      previousKeyRef.current = normalizedCurrentKey;
    }
  }, [currentKey, updateTaskKey, internalId]);

  return null;
};

const AccordionItemFormBuilder: React.FC<{
  taskIndex: number;
  onEnableAccordionItem: (value: string) => void;
  onDisableAccordionItem: (value: string) => void;
}> = ({ taskIndex, onEnableAccordionItem, onDisableAccordionItem }) => (
  <FormToggleAccordion
    accordionValue="task-form-builder"
    itemIndex={taskIndex}
    itemType="task"
    onDisableAccordionItem={onDisableAccordionItem}
    onEnableAccordionItem={onEnableAccordionItem}
  >
    <TemplateFormEditor key={taskIndex} itemIndex={taskIndex} itemType="task" />
  </FormToggleAccordion>
);

export const DefineTasksStep: React.FC = () => {
  const step = getStepById("define-tasks");
  const hasPermission = useCheckFieldPermission();
  const { removeTaskFromAllWorkflowRules } = useWorkflowReferences();
  const [activeTaskIndex, setActiveTaskIndex] = useState<number>(0);
  const [enabledAccordionItems, setEnabledAccordionItems] = useState<string[]>([
    "task-info",
    "task-form-builder",
  ]);
  const {
    openModal: openDeleteTaskModal,
    isOpen: isDeleteTaskModalOpen,
    closeModal: closeDeleteTaskModal,
    data: taskToDelete,
  } = useModal<TaskWithIndex>();

  const { fields, append, remove } = useFieldArray<
    CaseTemplateFormValues,
    "attributes.case_tasks",
    "_id"
  >({
    name: `attributes.case_tasks`,
    keyName: "_id",
  });

  const onAddTask = () => {
    append(
      {
        key: "",
        display_name: "",
        description: "",
        _internalId: generateInternalId(),
        _isNewlyAppended: true,
      },
      { focusName: `attributes.case_tasks.${fields.length}.display_name` },
    );

    setActiveTaskIndex(fields.length);
  };

  const handleRemoveTask = (taskIndex: number) => {
    const task = fields[taskIndex];
    if (task._internalId) {
      removeTaskFromAllWorkflowRules(task._internalId);
    }
    remove(taskIndex);
    setActiveTaskIndex(clamp(taskIndex - 1, 0, fields.length - 1));
  };

  const onDeleteTask = (taskIndex: number) => {
    const task = fields[taskIndex];
    if (task._isNewlyAppended) {
      handleRemoveTask(taskIndex);
    } else {
      openDeleteTaskModal({ ...task, index: taskIndex });
    }
  };

  const onDisableAccordionItem = useCallback((value: string) => {
    setEnabledAccordionItems((prevValues) =>
      prevValues.filter((v) => v !== value),
    );
  }, []);

  const onEnableAccordionItem = useCallback((value: string) => {
    setEnabledAccordionItems((prevValues) => {
      if (!prevValues.includes(value)) {
        return [...prevValues, value];
      }
      return prevValues;
    });
  }, []);

  return (
    <>
      <FormStepWrapper
        description={step.description}
        docLink={step.docLink}
        title={step.title}
      >
        {fields.length ? (
          <div className="flex gap-4">
            <Tasks
              activeTaskIndex={activeTaskIndex}
              fields={fields}
              onAddTask={onAddTask}
              onRemoveTask={onDeleteTask}
              onSelectTask={setActiveTaskIndex}
            />
            <div>
              <Divider orientation="vertical" variant="dark" />
            </div>
            <div className="w-full min-w-0 flex-1">
              <Accordion
                value={enabledAccordionItems}
                onChange={(value) => setEnabledAccordionItems(value)}
              >
                <Accordion.Item
                  key={`info-${activeTaskIndex}`}
                  dataLoc="task-info-accordion"
                  label="Define task name and description"
                  value="task-info"
                >
                  <TaskKeyWatcher
                    internalId={fields[activeTaskIndex]._internalId}
                    taskIndex={activeTaskIndex}
                  />
                  <TemplateItemInfo
                    descriptionDisabled={
                      !hasPermission(
                        "edit",
                        `attributes.case_tasks.${activeTaskIndex}.description`,
                      )
                    }
                    descriptionPlaceholder="Write a description of the task for the reviewer"
                    itemIndex={activeTaskIndex}
                    itemType="task"
                    keyDisabled={
                      !hasPermission(
                        "edit",
                        `attributes.case_tasks.${activeTaskIndex}.key`,
                      )
                    }
                    nameDisabled={
                      !hasPermission(
                        "edit",
                        `attributes.case_tasks.${activeTaskIndex}.display_name`,
                      )
                    }
                  />
                </Accordion.Item>
                <AccordionItemFormBuilder
                  key={`form-builder-${activeTaskIndex}`}
                  taskIndex={activeTaskIndex}
                  onDisableAccordionItem={onDisableAccordionItem}
                  onEnableAccordionItem={onEnableAccordionItem}
                />
              </Accordion>
            </div>
          </div>
        ) : (
          <div className="h-110">
            <EmptyState
              actionButton={{
                icon: faPlus,
                onClick: onAddTask,
                label: "Add task",
                isDisabled: !hasPermission("add", "attributes.case_tasks"),
              }}
              dataLoc="empty-state"
              description="Define tasks to be completed in this status"
              headline="No tasks defined yet"
              icon={faSearch}
            />
          </div>
        )}
      </FormStepWrapper>
      <DeleteTaskModal
        open={isDeleteTaskModalOpen}
        task={taskToDelete}
        onClose={closeDeleteTaskModal}
        onRemoveTask={handleRemoveTask}
      />
    </>
  );
};

type TaskWithIndex = CaseTemplateTaskFormValue & {
  index: number;
};

const DeleteTaskModal: React.FC<{
  task?: TaskWithIndex;
  open: boolean;
  onClose: () => void;
  onRemoveTask: (index: number) => void;
}> = ({ task, open, onClose, onRemoveTask }) => {
  return (
    <TextConfirmationModal
      challengeText={task?.display_name}
      description="Are you sure you want to delete this task? This action is not reversible. Any workflows currently using this task will also be affected."
      label="Enter task name to confirm"
      open={open}
      placeholder={task?.display_name}
      title={`Delete ${task?.display_name}`}
      onClose={onClose}
      onConfirm={() => task && onRemoveTask(task.index)}
    />
  );
};
